// The replay FILM (R531): the combat log played on the live engine.
//
// R528 gave the replay a card — a 2D panel that draws the terrain of the
// window one time and the hulls and hits each frame. This is the second
// mode of the same frame. It plays on the map's OWN graphics: the hull
// silhouettes of the atlas, the round style the player picked, the blasts
// of the effects layer and the bioluminescent wake, all on the terrain the
// engine already has on the GPU.
//
// **One context.** A second WebGL2 context would duplicate the atlas and
// the terrain plane, and a browser that runs out of contexts drops the
// oldest — which on a telephone is the map the player is playing on. So
// the film borrows the running engine: it builds five layers of its own
// and puts them in the five slots the live layers hold, through
// `engine.setLayer`. `FleetLayer`, `TorpedoLayer`, `FxLayer`, `WakeLayer`
// and `MonsterLayer` define no `dispose`, so that swap is reversible. The terrain
// and the territory layers DO dispose, so the film never touches them.
//
// **What the map shows under a film** is terrain, the structures and the
// fight. The sovereignty, the sonar, the heat map, the seaquake, the
// control beams, the cuts, the player effects of the hulls, the guard
// marks, the markers, the crosshair, the arrival and the two hex views
// are hidden for the length of the film and put back at the state
// they were found in. Hiding the territory un-fuses the terrain by
// construction (`layers/terrain.ts`), so the sea draws alone at no extra
// cost. A fight that is over must not carry the sovereignty of the season
// running now, which is the objection R528 raised and this answers.
//
// **The holds and the faction installations stay** (R542). The site layer
// draws the map's fixed structures, and a fight reads wrong on empty
// water: a commander must see what the hulls fought over. A replay names
// no site, so the marks are the sites of TODAY and their owner tint is
// today's tint. The layer takes its own per-frame feed from the engine,
// which drives every visible layer, and it reads nothing of the hidden
// layers: a mark's colour is the `Site` record's own faction.
//
// **The film simulates nothing.** Every record it hands a layer is read
// off the `Replay` frame the server sent, through the pure helpers of
// `ui/replay.ts` — `fitOf`, `poseAt`, `torpedoAt`, `sinkTimes` — so both
// modes read one set of rules and cannot drift.
//
// **The film pays the map scale** (R534). A replay decisecond is a MAP
// decisecond, and a world runs its map faster than wall time, so the pace
// is `play_rate_x × tactical_scale` and the timecode counts the wall
// seconds the commander watched.
//
// **The camera follows the fight** (R534, R541). Every frame the film
// takes the bounds of what it just drew and tests them at TWO fixed
// cameras. The fight fits at the opening window and has fitted there for
// the dwell: the camera eases home. It fits where the camera stands: the
// camera holds. It fits at neither: the camera eases out toward a window
// that holds all of it, and the zoom only ever goes out.
//
// **The chrome is DOM** (ui-map-interface.md §1). Letterbox bars with
// sprocket holes, a grain, a blinking REPLAY tag, a timecode, a loop
// counter and the transport. Nothing here draws text on the canvas.
//
// Every figure rides `data/presentation/presentation.toml
// [presentation.replay]` and every word rides `data/locale/en/ui.toml
// [ui.replay]`.

import {
  DamageKind, NpcKind, SeaObjectKind, type FleetState, type Replay, type ReplayHit, type ReplayTrack,
  type TorpedoState,
} from '../gen/proto/aquaphobia';
import { MPX_PER_PX, factionColor, factionName, hex, pxToMpx } from '../world';
import { reducedMotion } from '../render/motion';
import {
  fitOf, isFiredDamage, playRateDsPerMs, poseAt, sinkTimes, torpedoAt, withdrawTimes,
  type Bounds, type Fit, type Pose,
} from '../ui/replay';
// The text-form glyph helper (R579) is shared with the media player since R852.
import { textGlyph } from '../ui/glyph';
import { viewportRect } from '../ui/uiscale';
import { matchKey } from '../keys';
import { replay as S } from '../gen/strings';
import { isoTime } from '../locale/stamp';
import {
  CAMERA_MAX_ZOOM, CAMERA_MIN_ZOOM, KEYS, REPLAY_CAMERA_EASE_MS, type FilmKeyId,
  REPLAY_CAMERA_HOME_DWELL_MS, REPLAY_FILM_FIGURE_SCALE, REPLAY_FILM_FX_HOLD_ZOOM,
  REPLAY_HIT_PULSE_DS, REPLAY_LOOP_HOLD_MS, REPLAY_MARGIN_FRAC,
  REPLAY_MIN_WINDOW_PX, REPLAY_PLAY_RATE_X,
} from '../gen/presentation';
import { seaKindFigures } from '../seaobjects';
import type { FrameInfo, Layer } from './layer';
import type { BatchSource } from './layers/batches';
import { FleetLayer } from './layers/fleets';
import { FxLayer, packRound } from './layers/fx';
import { DETONATION_AUTO, type DetonationRound } from '../render/detonation-styles';
import { DamageLayer, type DamageHit } from './layers/damage';
import {
  CombatLayer, type CombatEvent, type CombatFeeds,
} from './layers/combat';
import { TorpedoLayer } from './layers/torpedoes';
import { WakeLayer } from './layers/wake';
import { MonsterLayer } from './layers/monsters';
import { monsterKindOf } from '../render/monster-kinds';
import { SapperWorkLayer } from './layers/sapperwork';
import type { TorpedoStyle } from '../ui/settings';

/** Playback speed at a map scale of one. `playRateDsPerMs` pays the
 *  world's own map scale on top of it (R534). */
export const PLAY_RATE_DS_PER_MS = playRateDsPerMs(1);

/**
 * The five slots the film borrows, and the layers of the map that stand in
 * them. None of the four classes disposes anything, so the swap in and the
 * swap out are both harmless.
 *
 * The film's own fleet layer draws NO heal beam, NO regen wave and NO plus
 * (R725). A record of the film carries no `tending` and no `aura`, the
 * layer's `tenderAuraPx` stays 0, and the damage layer answers `heal` with
 * nothing while it is borrowed. A film that invented the three marks would
 * state a trade the record never held.
 *
 * The `monsters` slot is the fifth (R2719): the film's own monster layer
 * draws the body of each monster of the record, its kind from the class
 * of the record, design 0, and the act from its motion, because a film
 * record carries no design and no act. The live monster layer would draw
 * today's monsters at the points of the live fleet layer.
 */
export const FILM_SLOTS = ['fleets', 'torpedoes', 'fx', 'wake', 'monsters'] as const;

/**
 * Every slot the film hides while it runs, in the engine's own slot order.
 *
 * `background`, `terrain`, `fog`, `haze`, `snow` and `vignette` are NOT
 * here: the water and the fluff are the sea the fight happened in, and the
 * player's own OPTIONS switches already decide what of it draws.
 *
 * The `damage` slot is not here either (R538): the film borrows the live
 * figure layer and feeds it the figures of the fight it plays.
 *
 * The `sites` slot left this list at R542: the holds and the faction
 * installations stay on the picture, at today's owner tint.
 *
 * The two chart slots are NOT here either (R548). The furniture is one
 * roster in two layers, and a film keeps HALF of it: the isobaths, the
 * place names, the ribbons and the rest of the terrain furniture are the
 * sea the fight happened in. So the controller reads the film flag and
 * gates out every piece whose `film` is `hide`, one piece at a time, and
 * the two layers stay visible for the ones it keeps.
 *
 * The `deadzone` slot is here (R1472): the live fleet layer packs it, and
 * a film borrows the `fleets` slot, so the live layer stops its update and
 * the disc of the live selection would stand still over the fight.
 *
 * The `guard` slot is here (R2401): the live guard layer draws the clock
 * of today's own guards at the live fleet layer's points, so a set
 * Sentinel's pip would stand on the water of the fight. A film plays no
 * round kill either (§6.3.6), so the slot has nothing of the fight.
 *
 * The three gather slots and the `effects` slot are here (R2402) for the
 * same reason: each one walks the hulls of the live fleet layer, which
 * holds its last points while the film borrows `fleets`. A replay records
 * no cut and no player effect, so the slots have nothing of the fight.
 * `holdeffects` stays, because it dresses the sites of today (R542).
 *
 * The `signature` slot is here (R2408) for the same reason: it draws the
 * looks of today's hulls at the live fleet layer's points. A replay
 * records no signature state of a hull, so the slot has nothing of the
 * fight.
 *
 * The `survey` slot is here (R2626): the hatch is the survey of today's
 * faction, and the survey is faction knowledge that no film shows (v2
 * §4.1 "Privacy").
 *
 * The `seaobjects` slot is here (R2657): it draws the sea objects of
 * today's picture, and a replay records none. The `sapperwork` slot is
 * NOT here: the film borrows the live layer, which drops the work of
 * today's Sappers while a film plays (`setFilming`), and hands it the sea
 * mine hits of the fight it plays.
 */
export const FILM_HIDDEN_SLOTS = [
  'creatures', 'territory', 'deadzone', 'survey', 'kraken', 'hexfills', 'hexborders', 'heatmap', 'hovercell',
  'quake', 'control', 'sonar', 'gatherunder', 'seaobjects', 'gathermid', 'gatherover', 'signature', 'effects', 'guard',
  'markers', 'orderdrag', 'plot', 'crosshair',
  'spawn',
] as const;

// ---------------------------------------------------------------------------
// The pure half. Every function here is a function of the replay frame and
// a time, so `film.test.ts` reads them with no canvas and no context.
// ---------------------------------------------------------------------------

/**
 * The zoom that fits the window on a canvas of `w` by `h` CSS px.
 *
 * The fit is square (R528), so the SHORT axis holds it whole and the long
 * axis simply shows more water. `Camera.centerOn` clamps as well; the
 * clamp is repeated here so the answer can be stated and tested.
 */
export function filmZoom(fit: Fit, w: number, h: number): number {
  const short = Math.max(1, Math.min(w, h));
  const z = short / (2 * Math.max(1e-6, fit.half));
  return Math.min(CAMERA_MAX_ZOOM, Math.max(CAMERA_MIN_ZOOM, z));
}

/** Where the film's camera stands: the world point at the centre of the
 *  glass, and the zoom. The film keeps its own copy, because the camera
 *  takes a write and answers no read. */
export interface FilmCam { cx: number; cy: number; zoom: number }

/**
 * The window the fight wants, in world px: its own bounds, with the replay
 * margin around them, and never narrower than the minimum window.
 *
 * The SAME window decides whether the fight fits and what covering it
 * costs, so the camera settles where it exactly fits instead of hunting.
 */
function fightWindow(bounds: Bounds): { cx: number; cy: number; halfX: number; halfY: number } {
  const margin = 1 + REPLAY_MARGIN_FRAC;
  return {
    cx: (bounds.x0 + bounds.x1) / 2,
    cy: (bounds.y0 + bounds.y1) / 2,
    halfX: Math.max(((bounds.x1 - bounds.x0) / 2) * margin, REPLAY_MIN_WINDOW_PX / 2),
    halfY: Math.max(((bounds.y1 - bounds.y0) / 2) * margin, REPLAY_MIN_WINDOW_PX / 2),
  };
}

/**
 * Does the fight's whole window fall inside `safe` from where `cam`
 * stands? The one fit test of the film (R541): `frameTarget` asks it of
 * `home` and of the camera of the frame, and the follow asks it of `home`
 * to run the dwell.
 */
export function fitsAt(bounds: Bounds, safe: Bounds, cam: FilmCam): boolean {
  const { cx, cy, halfX, halfY } = fightWindow(bounds);
  const z = Math.max(1e-6, cam.zoom);
  return (cx - halfX - cam.cx) * z >= safe.x0
    && (cx + halfX - cam.cx) * z <= safe.x1
    && (cy - halfY - cam.cy) * z >= safe.y0
    && (cy + halfY - cam.cy) * z <= safe.y1;
}

/**
 * The camera the film should ease toward.
 *
 * `safe` is the rectangle the fight may use, in CSS px measured FROM THE
 * CENTRE of the glass: the viewport less the two letterbox bars and less
 * the damage report. The camera puts its own centre at the centre of the
 * glass, so a rectangle that is not centred moves the target instead of
 * the zoom.
 *
 * The answer is one of three (R541):
 *
 * - `home`, the window the film opened on, when the fight fits inside
 *   `safe` AS SEEN FROM HOME and the dwell has run out (`homeReady`).
 * - `current`, unchanged, when the fight fits where the camera stands but
 *   not at home. The camera HOLDS. It must not walk toward a window the
 *   fight spills out of, because it would be pushed straight back.
 * - a window that covers `bounds` with the replay margin, never tighter
 *   than the minimum window, when the fight fits at neither. The zoom only
 *   ever goes OUT: a fight that spreads must not be chased in and out on
 *   every frame.
 *
 * The fit is tested against a FIXED camera and never against the moving
 * one alone. R534 tested it against the camera of the frame, so a fight
 * that fitted here but not at home flipped the target between the two on
 * every frame and the camera hunted for ever at the ease rate.
 */
export function frameTarget(
  bounds: Bounds, safe: Bounds, current: FilmCam, home: FilmCam = current,
  homeReady = true,
): FilmCam {
  if (homeReady && fitsAt(bounds, safe, home)) return home;
  if (fitsAt(bounds, safe, current)) return current;
  const { cx, cy, halfX, halfY } = fightWindow(bounds);
  const halfW = Math.max(1, (safe.x1 - safe.x0) / 2);
  const halfH = Math.max(1, (safe.y1 - safe.y0) / 2);
  let zoom = Math.min(halfW / halfX, halfH / halfY);
  // Out, and never in: a fight that spreads must not be chased in and out
  // again on the next frame.
  zoom = Math.min(current.zoom, zoom);
  zoom = Math.min(CAMERA_MAX_ZOOM, Math.max(CAMERA_MIN_ZOOM, zoom));
  // The safe rect is not centred on the glass, and the camera centres what
  // it is given, so the offset of the rect moves the target instead.
  const ox = (safe.x0 + safe.x1) / 2;
  const oy = (safe.y0 + safe.y1) / 2;
  return { cx: cx - ox / zoom, cy: cy - oy / zoom, zoom };
}

/** What counts as a camera that has stopped: less than a tenth of a world
 *  px of pan, or a ten-thousandth of a zoom, in one frame. */
export const CAM_STILL_PX = 0.1;
export const CAM_STILL_ZOOM = 1e-4;

/** The share of a side that must change before a re-fit snaps the camera
 *  home (R580). A telephone fires `resize` when the browser chrome
 *  collapses or expands and when the application comes back from the
 *  background, and neither of those changed the fight. A tenth of a side is
 *  more than that chrome and less than a rotation or a split screen. */
const BOX_RESHAPE_FRAC = 0.1;

/** The box a fit was made for, in CSS px. */
export interface FilmBox { w: number; h: number }

/**
 * Did the canvas box really change shape (R580)?
 *
 * `true` when either side moved by more than `frac` of what it was, and
 * always `true` when no fit has been made yet. A `false` answer keeps the
 * camera and the dwell where the follow left them, and the film only
 * re-measures the glass.
 */
export function boxReshaped(
  was: FilmBox | null, now: FilmBox, frac = BOX_RESHAPE_FRAC,
): boolean {
  if (!was) return true;
  const moved = (a: number, b: number) => Math.abs(b - a) > Math.max(1, Math.abs(a)) * frac;
  return moved(was.w, now.w) || moved(was.h, now.h);
}

/** The least glass a fight may be left with when the report is taken off
 *  the foot (R581). A strip of water 40 px tall reads as no picture at all,
 *  so under this the film takes the whole glass and lets the panel stand
 *  over the water instead. */
export const SAFE_MIN_FIGHT_PX = 120;

/**
 * The glass the fight may use, in CSS px from the top left (R581).
 *
 * The viewport, less the two letterbox bars, less the damage report where
 * it stands. The report is subtracted by WHERE IT IS ANCHORED and not by a
 * test of the screen's half: a panel whose left edge is right of the
 * centre stands beside the fight, and every other panel stands at its
 * foot. R534 tested `report.top > h / 2`, so a report taller than half the
 * screen — which is what a telephone gets since the two-row rows — was
 * subtracted from nothing at all and the fight drew behind it.
 *
 * A foot panel is only subtracted while at least `minPx` of glass is left
 * above it. Past that the film falls back to the whole glass between the
 * bars, because a fight squeezed into a strip is worse than a fight the
 * panel partly covers.
 */
export function safeGlass(
  w: number, h: number, bar: number, report: Bounds | null,
  minPx = SAFE_MIN_FIGHT_PX,
): Bounds {
  const full: Bounds = { x0: 0, y0: bar, x1: w, y1: h - bar };
  let { x0, y0, x1, y1 } = full;
  if (report && report.x1 > report.x0 && report.y1 > report.y0) {
    if (report.x0 > w / 2) x1 = Math.min(x1, report.x0);
    else if (report.y0 - y0 >= minPx) y1 = Math.min(y1, report.y0);
  }
  if (x1 - x0 < 1 || y1 - y0 < 1) return { x0: 0, y0: 0, x1: w, y1: h };
  return { x0, y0, x1, y1 };
}

/**
 * The dwell clock of the way home (R541), in ms.
 *
 * The camera goes back to the opening window only after the fight has
 * fitted there without a break for `camera_home_dwell_ms`. One round in
 * the water widens what the film draws for a beat and the detonation takes
 * it away again, so a camera that went home the frame a fight fitted would
 * pump in and out for the length of a busy exchange.
 *
 * `held` is what the last frame answered, and the answer is what this
 * frame holds. A fight that spills at home puts the clock back to nought.
 */
export function homeDwell(held: number, fitsAtHome: boolean, dtMs: number): number {
  if (!fitsAtHome) return 0;
  return held + Math.max(0, dtMs);
}

/** Has the dwell run out? The camera may go home. */
export function homeDue(held: number, dwellMs = REPLAY_CAMERA_HOME_DWELL_MS): boolean {
  return held >= dwellMs;
}

/** The share of the gap a frame of `dtMs` closes. Exponential, so the
 *  ease is the same movement at any frame rate (R534). */
export function easeFraction(dtMs: number, easeMs = REPLAY_CAMERA_EASE_MS): number {
  if (!(dtMs > 0)) return 0;
  return 1 - Math.exp(-dtMs / Math.max(1, easeMs));
}

/** The one answer of `recordBounds`, rewritten every frame (R131). */
const BOUNDS_BUF: Bounds = { x0: 0, y0: 0, x1: 0, y1: 0 };

/** Grows `b` to hold one wire point. */
function growBounds(b: Bounds, xm: number, ym: number): void {
  const x = xm / MPX_PER_PX, y = ym / MPX_PER_PX;
  if (x < b.x0) b.x0 = x;
  if (x > b.x1) b.x1 = x;
  if (y < b.y0) b.y0 = y;
  if (y > b.y1) b.y1 = y;
}

/** What the film draws this frame, in world px: every hull pose and every
 *  round in the water. Null when the frame holds neither. The answer is
 *  one reused object: a frame reads it and keeps nothing. */
export function recordBounds(rec: FilmRecords): Bounds | null {
  const b = BOUNDS_BUF;
  b.x0 = Infinity; b.y0 = Infinity; b.x1 = -Infinity; b.y1 = -Infinity;
  for (const f of rec.fleets) growBounds(b, f.xMpx, f.yMpx);
  for (const t of rec.torpedoes) growBounds(b, t.xMpx, t.yMpx);
  return Number.isFinite(b.x0) ? b : null;
}

/** Where the film stands: its time, its intent and its loop count. */
export interface FilmClock {
  /** Replay deciseconds. */
  t: number;
  /** Does the player want it to run? */
  playing: boolean;
  /** How many times it has played from the start. The first pass is 1. */
  loops: number;
  /** Milliseconds left of the hold on the last frame; 0 while it runs. */
  holdMs: number;
}

/** What one step of the clock did. `seeked` means the time JUMPED. */
export interface FilmStep {
  clock: FilmClock;
  seeked: boolean;
}

/**
 * Moves the film's clock one frame on.
 *
 * A paused film holds. A running film consumes `dtMs` of wall time at the
 * playback rate. At the end it holds `holdMs`, and the hold is what makes
 * the restart read as a loop and not as a stutter; then it goes back to
 * zero, counts the pass and plays again.
 *
 * It is pure, so the loop is stated in one place and read back by the test
 * rather than watched in a browser.
 */
export function stepClock(
  c: FilmClock, dtMs: number, durationDs: number, holdMs = REPLAY_LOOP_HOLD_MS,
  rateDsPerMs = PLAY_RATE_DS_PER_MS,
): FilmStep {
  const end = Math.max(0, durationDs);
  if (!c.playing) return { clock: c, seeked: false };
  if (c.holdMs > 0) {
    const left = c.holdMs - dtMs;
    if (left > 0) return { clock: { ...c, holdMs: left }, seeked: false };
    return { clock: { t: 0, playing: true, loops: c.loops + 1, holdMs: 0 }, seeked: true };
  }
  const t = c.t + dtMs * rateDsPerMs;
  if (t >= end) {
    return { clock: { t: end, playing: true, loops: c.loops, holdMs: Math.max(1, holdMs) }, seeked: false };
  }
  return { clock: { ...c, t }, seeked: false };
}

/**
 * The heading of a track's last leg, in radians with +x the bow (R1337). The
 * last step with a real move rules, so a hull that stood still at the end
 * keeps the bearing it came in on, as the live map keeps it. A track with
 * fewer than two places, or with no move at all, reads 0.
 */
export function lastLegHeading(tr: ReplayTrack): number {
  const pts = tr.points;
  for (let i = pts.length - 1; i > 0; i--) {
    const dx = pts[i].xMpx - pts[i - 1].xMpx;
    const dy = pts[i].yMpx - pts[i - 1].yMpx;
    if (dx !== 0 || dy !== 0) return Math.atan2(dy, dx);
  }
  return 0;
}

/** Where one hull of the frame is at `tDs`, or null when it is not on film. */
export function poseOf(r: Replay, hull: number, tDs: number): Pose | null {
  for (const tr of r.tracks) if (tr.hull === hull) return poseAt(tr, tDs);
  return null;
}

/**
 * The records one frame of the film hands the layers.
 *
 * The arrays and the objects in them are reused between frames (R131): a
 * consumer reads them inside the frame and keeps no reference.
 */
export class FilmRecords {
  readonly fleets: FleetState[] = [];
  readonly removed: number[] = [];
  readonly torpedoes: TorpedoState[] = [];
  readonly effects: FilmEffect[] = [];
  /** The damage figures of this frame (R538). */
  readonly damage: DamageHit[] = [];
}

/** One thing the effects layer is asked to fire this frame. */
export interface FilmEffect {
  kind: 'launch' | 'detonation' | 'ring' | 'hit' | 'mark' | 'explosion' | 'seamine';
  x: number;
  y: number;
  faction: number;
  /**
   * What a gun hit and a sinking hand the gun-shot layer (R1007). A record of
   * another kind leaves them at 0.
   *
   * `sx`/`sy` is the shooter's point (a `detonation` carries the launch
   * point of its round too, R2842), `victim` the struck hull's SIM FLEET ID
   * (the id the film's own fleet records carry, so the layer can follow it),
   * `cls` the shooter's real class — a replay names every hull in it, so the
   * film states the family and the layer resolves nothing (R1009) — and
   * `shieldPct` what the victim still held, which picks the hit look.
   */
  sx: number;
  sy: number;
  victim: number;
  victimFaction: number;
  cls: number;
  shieldPct: number;
  /** The kind of the round of a `detonation`, 0 on every other record: a
   *  Torpor's detonation draws its slow ring (R2783). */
  torpedo: number;
  /** The sea mine kind of a `seamine`, `seaMineKindOfHit` (R2657); 0 on
   *  every other record. */
  seaMine: number;
}

/** The faction that fired a record, or 0 where the log names no hull. */
function sourceFaction(r: Replay, source: number): number {
  return source >= 0 ? r.hulls[source]?.faction ?? 0 : 0;
}

/** The empty time map: a film that states no withdrawal. */
const NO_TIMES: ReadonlyMap<number, number> = new Map();

/**
 * The fleet records of `tDs`, and the ids of the hulls that are gone.
 *
 * A hull is absent before its first sample, because the log had not
 * started writing it down. A hull the window sank is REMOVED from the
 * time it sank, so the layer fades it out where the blast went off; the
 * layer's own fade is what takes it off the water. A hull that broke off
 * a window battle (`withdrawn`, R1451) is removed from the time it left
 * the arena the same way, and no blast marks it.
 *
 * `own` is false for every hull: nobody is flying in a film, so no order
 * line, no patrol lane and no home leg is ever drawn.
 */
export function fleetRecordsAt(
  r: Replay, tDs: number, sunk: ReadonlyMap<number, number>, out: FilmRecords,
  withdrawn: ReadonlyMap<number, number> = NO_TIMES,
): void {
  let n = 0;
  out.removed.length = 0;
  for (const tr of r.tracks) {
    const hull = r.hulls[tr.hull];
    if (!hull) continue;
    const pose = poseAt(tr, tDs);
    if (!pose) continue;
    const sank = sunk.get(tr.hull);
    if (sank !== undefined && tDs >= sank) { out.removed.push(hull.id); continue; }
    const left = withdrawn.get(tr.hull);
    if (left !== undefined && tDs >= left) { out.removed.push(hull.id); continue; }
    let f = out.fleets[n];
    if (!f) { f = blankFleet(); out.fleets[n] = f; }
    n++;
    f.id = hull.id;
    f.faction = hull.faction;
    // The real class, so every hull wears the silhouette of R426 rather
    // than the dart of a contact nobody read.
    f.cls = hull.cls;
    // The NPC kind, so a hull of the NPC side wears its NPC mark in the
    // NPC ink as on the live map, and a monster is no ship hull (R2622).
    f.npc = hull.npc;
    f.xMpx = pxToMpx(pose.x);
    f.yMpx = pxToMpx(pose.y);
    f.hullPct = Math.round(Math.max(0, Math.min(100, pose.hull)));
    f.shieldPct = Math.round(Math.max(0, Math.min(100, pose.shield)));
    f.name = hull.name;
    f.ownerName = hull.commander;
  }
  out.fleets.length = n;
}

/**
 * The rounds in the water at `tDs`.
 *
 * One record per damage record of the torpedo kind, from its launch point
 * to the victim point. A splash is a second damage record on a neighbour
 * and carries no run, so it leaves a ring and never a round.
 */
export function torpedoRecordsAt(r: Replay, tDs: number, out: FilmRecords): void {
  let n = 0;
  for (let i = 0; i < r.hits.length; i++) {
    const hit = r.hits[i];
    if (hit.kind !== DamageKind.DAMAGE_KIND_TORPEDO) continue;
    const at = torpedoAt(hit, tDs);
    if (!at) continue;
    let t = out.torpedoes[n];
    if (!t) { t = blankTorpedo(); out.torpedoes[n] = t; }
    n++;
    // The id is the hit's own index, lifted clear of every sim fleet id
    // the live layer might still hold.
    t.id = TORPEDO_ID_BASE + i;
    t.faction = sourceFaction(r, hit.source);
    t.kind = hit.torpedo;
    t.xMpx = pxToMpx(at.x);
    t.yMpx = pxToMpx(at.y);
    t.headingMrad = Math.round(
      Math.atan2(hit.vyMpx - hit.syMpx, hit.vxMpx - hit.sxMpx) * 1000,
    );
    const span = Math.max(1e-6, hit.t1Ds - hit.launchTDs);
    t.runPct = Math.max(0, Math.min(100, ((tDs - hit.launchTDs) / span) * 100));
  }
  out.torpedoes.length = n;
}

/** Where a film's round ids start. */
export const TORPEDO_ID_BASE = 1_000_000;

/** How far below a seek the effect window starts. See `seek`. */
const SEEK_EPS_DS = 1e-6;

/**
 * Everything that fires in the half-open window `(t0, t1]`.
 *
 * The WINDOW is the latch. An effect fires on the one frame its time falls
 * inside, so it fires exactly once per pass of the loop, and a seek — which
 * moves both ends together — fires nothing at all. No set of fired keys is
 * kept, so nothing has to be cleared and nothing can be left behind.
 *
 * A gun run is one damage record with a shot count over a span of time, so
 * the film paces its pulse at `REPLAY_HIT_PULSE_DS` rather than inventing a
 * time for every shot.
 */
export function effectsBetween(
  r: Replay, t0: number, t1: number, sunk: ReadonlyMap<number, number>,
  out: FilmRecords, pulseDs = REPLAY_HIT_PULSE_DS,
): void {
  let n = 0;
  for (const hit of r.hits) {
    const faction = sourceFaction(r, hit.source);
    if (hit.kind === DamageKind.DAMAGE_KIND_GUN) {
      // A run pulses at `t0 + k · pulseDs`, for every whole step that still
      // falls inside the run. The window picks the steps of this frame, so
      // a pulse fires once however long or short the frame was.
      const step = Math.max(1e-6, pulseDs);
      const last = Math.floor(Math.max(0, hit.t1Ds - hit.t0Ds) / step);
      const from = Math.max(0, Math.floor((t0 - hit.t0Ds) / step) + 1);
      const to = Math.min(last, Math.floor((t1 - hit.t0Ds) / step));
      if (from > to) continue;
      // A gun pulse is one SALVO of the gun-shot layer (R1007), so it carries
      // the shooter's point and class and the victim's shields with it.
      const v = victimAt(r, hit, t1);
      const shooter = hit.source >= 0 ? r.hulls[hit.source] : undefined;
      const struck = r.hulls[hit.victim];
      for (let k = from; k <= to; k++) {
        const e = effectAt(out, n++, 'hit', v.x, v.y, faction);
        e.sx = hit.sxMpx / MPX_PER_PX;
        e.sy = hit.syMpx / MPX_PER_PX;
        e.victim = struck ? struck.id : 0;
        e.victimFaction = struck ? struck.faction : 0;
        e.cls = shooter ? shooter.cls : 0;
        e.shieldPct = v.shield;
      }
      continue;
    }
    if (hit.kind === DamageKind.DAMAGE_KIND_TORPEDO) {
      if (fired(hit.launchTDs, t0, t1)) {
        effectAt(out, n++, 'launch', hit.sxMpx / MPX_PER_PX, hit.syMpx / MPX_PER_PX, faction);
      }
      if (fired(hit.t1Ds, t0, t1)) {
        const v = victimAt(r, hit, t1);
        const e = effectAt(out, n++, 'detonation', v.x, v.y, faction);
        e.torpedo = hit.torpedo;
        // The launch point gives the look its course (R2842).
        e.sx = hit.sxMpx / MPX_PER_PX;
        e.sy = hit.syMpx / MPX_PER_PX;
      }
      continue;
    }
    if (!fired(hit.t1Ds, t0, t1)) continue;
    const v = victimAt(r, hit, t1);
    // A sea mine hit (R2657, v2-the-deep.md §11.7 "The record"): the
    // detonation look at the struck hull, of the kind its damage names. The
    // wire of the Cheval is a mark: it bites each combat minute.
    if (hit.kind === DamageKind.DAMAGE_KIND_SEA_MINE) {
      effectAt(out, n++, 'seamine', v.x, v.y, faction).seaMine = seaMineKindOfHit(hit);
      continue;
    }
    if (hit.kind === DamageKind.DAMAGE_KIND_CHEVAL) {
      effectAt(out, n++, 'mark', v.x, v.y, faction);
      continue;
    }
    // A splash rings its neighbour and a window strips a shield: both read
    // as a wave rather than as a strike. A gun splash is one small mark and
    // no shot, as on the live map. A damage that nothing fired — a shock, a
    // grind, a cost a hull pays itself — fires no effect: its source point
    // is the corner of the map, and the hull bar alone shows the loss.
    const wave = hit.kind === DamageKind.DAMAGE_KIND_TORPEDO_SPLASH
      || hit.kind === DamageKind.DAMAGE_KIND_WINDOW;
    if (wave) effectAt(out, n++, 'ring', v.x, v.y, faction);
    else if (isFiredDamage(hit.kind)) effectAt(out, n++, 'mark', v.x, v.y, faction);
  }
  // The sinkings, in hull order, so one frame always fires them the same
  // way (the determinism rule of the root CLAUDE.md).
  for (let hull = 0; hull < r.hulls.length; hull++) {
    const at = sunk.get(hull);
    if (at === undefined || !fired(at, t0, t1)) continue;
    const pose = poseOf(r, hull, at);
    if (!pose) continue;
    const sunkHull = r.hulls[hull];
    const e = effectAt(out, n++, 'explosion', pose.x, pose.y, sunkHull?.faction ?? 0);
    // The final blow of R1007 needs the hull it sinks, by its sim fleet id.
    e.victim = sunkHull ? sunkHull.id : 0;
    e.victimFaction = sunkHull ? sunkHull.faction : 0;
  }
  out.effects.length = n;
}

/**
 * The sea mine kind of a `DAMAGE_KIND_SEA_MINE` record (R2657). A record
 * names no kind, but each kind deals its own lump (`SeaObjectSpec.damage` of
 * the hello catalog, R2659, numbers.md §4): a hull that lived took the whole
 * lump, so the kind whose lump it is;
 * a hull that sank took at most the lump, so the least kind whose lump
 * reaches what it took. A splash reads as the Petard or the Siren whose
 * lump is nearest above it. Answers the Barb when no kind fits.
 */
export function seaMineKindOfHit(hit: Pick<ReplayHit, 'damageX10' | 'hullAfterX10'>): number {
  const took = hit.damageX10 / 10;
  let best = SeaObjectKind.SEA_OBJECT_KIND_BARB as number, bestGap = Infinity;
  for (let kind = SeaObjectKind.SEA_OBJECT_KIND_BARB; kind <= SeaObjectKind.SEA_OBJECT_KIND_PETARD; kind++) {
    const k = seaKindFigures(kind);
    if (!k || !k.seaMine || !(k.damage > 0)) continue;
    const gap = k.damage - took;
    const fits = hit.hullAfterX10 > 0 ? Math.abs(gap) < 0.5 : gap > -0.5;
    if (fits && gap < bestGap) { best = kind; bestGap = gap; }
  }
  return best;
}

/** The one answer of `victimAt`, rewritten for every record that fires. */
const VICTIM = { x: 0, y: 0, shield: -1 };

/**
 * Where the struck hull of a record stands at `t`, and the shields it
 * holds: its pose on film, else the point the record names and -1. The
 * read walks every track, so `effectsBetween` asks it only for a record
 * that fires this frame. The answer is one reused object (R131).
 */
function victimAt(r: Replay, hit: ReplayHit, t: number): typeof VICTIM {
  const pose = poseOf(r, hit.victim, t);
  VICTIM.x = pose ? pose.x : hit.vxMpx / MPX_PER_PX;
  VICTIM.y = pose ? pose.y : hit.vyMpx / MPX_PER_PX;
  VICTIM.shield = pose ? pose.shield : -1;
  return VICTIM;
}

/** Is `at` inside the half-open window `(t0, t1]`? */
function fired(at: number, t0: number, t1: number): boolean {
  return at > t0 && at <= t1;
}

/**
 * The effect record at `n` of `out`, reset to one kind, one point and one
 * faction. The record is reused between frames (R131), so every field a
 * gun hit or a sinking writes after this call is put back to rest here.
 */
function effectAt(
  out: FilmRecords, n: number, kind: FilmEffect['kind'], x: number, y: number, faction: number,
): FilmEffect {
  let e = out.effects[n];
  if (!e) {
    e = {
      kind, x: 0, y: 0, faction: 0, sx: 0, sy: 0, victim: 0,
      victimFaction: 0, cls: 0, shieldPct: -1, torpedo: 0, seaMine: 0,
    };
    out.effects[n] = e;
  }
  e.kind = kind; e.x = x; e.y = y; e.faction = faction;
  e.sx = 0; e.sy = 0; e.victim = 0; e.victimFaction = 0; e.cls = 0; e.seaMine = 0;
  e.shieldPct = -1; e.torpedo = 0;
  return e;
}

// --- the damage figures -----------------------------------------------------
//
// The film draws the same figures the live map draws (R538): the number
// that pops at a struck hull, for the commander who is watching. The
// records come off the replay's own damage records, so the film invents no
// number and states what the log holds.

/**
 * Which damage records carry a figure.
 *
 * A gun hit, a gun splash, a torpedo and a torpedo splash — and nothing
 * else, which is the live map's own answer: a quake, a scan cost, a share
 * cost, a window and a settlement fire no hit event there, so they draw no
 * figure here (R538).
 */
const FIGURE_KINDS: readonly DamageKind[] = [
  DamageKind.DAMAGE_KIND_GUN, DamageKind.DAMAGE_KIND_GUN_SPLASH,
  DamageKind.DAMAGE_KIND_TORPEDO, DamageKind.DAMAGE_KIND_TORPEDO_SPLASH,
];

/**
 * Which figure a damage record deserves for the commander who watches.
 *
 * The live map classifies by player id, but a replay names no id: it
 * carries the commander's NAME on every hull and on the source of a base
 * tube. So the film compares names. A bot flies under an empty name, which
 * matches nobody, and a viewer the client cannot name gets no figure.
 *
 * `taken` when the struck hull is the viewer's, `dealt` when the viewer
 * fired, and NOTHING for a fight between two other empires — the same rule
 * the live map states (R538).
 */
export function classifyReplayDamage(
  r: Replay, hit: ReplayHit, myName: string,
): 'taken' | 'dealt' | null {
  if (myName === '') return null;
  if (!FIGURE_KINDS.includes(hit.kind)) return null;
  if (r.hulls[hit.victim]?.commander === myName) return 'taken';
  const source = hit.source >= 0 ? r.hulls[hit.source] : undefined;
  const fired = source ? source.commander : hit.sourceCommander;
  return fired === myName ? 'dealt' : null;
}

/**
 * Every figure that lands in the half-open window `(t0, t1]`.
 *
 * The window is the same latch the effects take: a figure fires on the one
 * frame its record's `t1_ds` falls inside, so it fires once per pass of the
 * loop and a seek fires none. A gun run is one record over a span, and it
 * lands its whole sum at the end of the run.
 */
export function damageBetween(
  r: Replay, t0: number, t1: number, myName: string, out: FilmRecords,
): void {
  let n = 0;
  for (const hit of r.hits) {
    if (!fired(hit.t1Ds, t0, t1) || hit.damageX10 <= 0) continue;
    const side = classifyReplayDamage(r, hit, myName);
    if (!side) continue;
    let h = out.damage[n];
    if (!h) { h = blankDamage(); out.damage[n] = h; }
    n++;
    h.victim = r.hulls[hit.victim]?.id ?? 0;
    h.x = hit.vxMpx / MPX_PER_PX;
    h.y = hit.vyMpx / MPX_PER_PX;
    h.amount = hit.damageX10 / 10;
    h.taken = side === 'taken';
    h.torpedo = hit.kind === DamageKind.DAMAGE_KIND_TORPEDO
      || hit.kind === DamageKind.DAMAGE_KIND_TORPEDO_SPLASH;
    h.splash = hit.kind === DamageKind.DAMAGE_KIND_GUN_SPLASH
      || hit.kind === DamageKind.DAMAGE_KIND_TORPEDO_SPLASH;
    h.faction = r.hulls[hit.victim]?.faction ?? 0;
    h.sourceX = hit.sxMpx / MPX_PER_PX;
    h.sourceY = hit.syMpx / MPX_PER_PX;
  }
  out.damage.length = n;
}

/** One figure record, at rest. */
function blankDamage(): DamageHit {
  return {
    x: 0, y: 0, victim: 0, amount: 0, taken: false, torpedo: false,
    splash: false, faction: 0, sourceX: 0, sourceY: 0,
  };
}

// --- the damage report ------------------------------------------------------
//
// The table is an ACCOUNT and not a log (R534): one row per source, with
// its counters summed. The server wrote the source phrase and the kind
// word (`source_text`, `kind_word`), and the film does the arithmetic, so
// a row and a line of the inbox message name one source the same way and
// the client still writes no sentence of its own.

/** One row of the film's damage report: one source, of one kind. */
export interface ReportRow {
  /** The source, its commander, its faction and the kind word, joined —
   *  the row's identity. */
  key: string;
  /** Wall clock of the first damage this source landed. */
  unix: number;
  kind: string;
  source: string;
  /** The commander the source answers to; empty for a faction bot. */
  commander: string;
  /** The source is a hull of a monster, so the COMMANDER cell reads the
   *  word `monster` and not `nobody` (R2815). The word stays out of
   *  `commander`, which classifyReplayDamage compares with a name. */
  monster: boolean;
  /** The banner the source sails under; 0 where the log names no hull. */
  faction: number;
  /** Volleys, summed: a gun run is one record with a shot count. */
  shots: number;
  /** Damage applied, in tenths, summed. */
  damageX10: number;
  /** The first record's before, and the last record's after, in tenths. */
  shieldBeforeX10: number;
  shieldAfterX10: number;
  hullBeforeX10: number;
  hullAfterX10: number;
}

/** A stored figure in tenths, as a report prints it: whole points. */
export function points(x10: number): string {
  return String(Math.round(x10 / 10));
}

/**
 * One word with its first letter in upper case (R535).
 *
 * The KIND cell opens a column and reads as a heading, so it takes a
 * capital. The word the server wrote stays lower case, because a line of
 * an inbox message prints it inside a sentence.
 */
export function capitalise(word: string): string {
  if (word === '') return '';
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/** What the FACTION cell of a row reads. A source the log names no hull
 *  for — the sea, the seaquake — sails under no banner and shows none. */
export function factionCell(faction: number): string {
  return faction > 0 ? factionName(faction) : '';
}

/** What the COMMANDER cell of a row reads: the commander, else `monster`
 *  for a hull of a monster (R2815), else `nobody` for a faction bot and a
 *  pirate. */
export function commanderCell(row: Pick<ReportRow, 'commander' | 'monster'>): string {
  return row.commander || (row.monster ? S.monster : S.nobody);
}

/**
 * What the tag reads while the loop holds (R535): the whole seconds left
 * of the hold, and nothing at all while the film runs.
 *
 * It is whole seconds on purpose. A film that wrote the milliseconds would
 * touch the DOM on every frame of the hold, and §10 forbids that.
 */
export function holdWord(holdMs: number): string {
  if (!(holdMs > 0)) return '';
  return S.restart_in({ n: Math.ceil(holdMs / 1000) });
}

/** The wall clock of a damage record, as the inbox message stamps it:
 *  `HH:MM:SS UTC` (R1544). An unstamped record shows no time. */
export function utcClock(unix: number): string {
  return isoTime(unix, { seconds: true, utc: true });
}

/** The hull indices the reference put in focus: the sunk hull, the struck
 *  hulls, the fleets a window weighed. The table counts these and no
 *  others, while the film DRAWS every hull of the frame (R534). */
export function focusHulls(r: Replay): Set<number> {
  const ids = new Set(r.ref?.fleets ?? []);
  const out = new Set<number>();
  for (let i = 0; i < r.hulls.length; i++) {
    if (ids.has(r.hulls[i].id)) out.add(i);
  }
  // A frame with no reference — or one whose hulls the log could not open
  // — reports every hull rather than nothing at all.
  if (out.size === 0) for (let i = 0; i < r.hulls.length; i++) out.add(i);
  return out;
}

/**
 * Which damage records the report accounts for, in the order the film
 * reveals them: a hit on a focus hull, earliest first. A record the
 * server wrote no words for is not in the report at all.
 */
export function reportOrder(r: Replay, focus: ReadonlySet<number>): number[] {
  const out: number[] = [];
  for (let i = 0; i < r.hits.length; i++) {
    const hit = r.hits[i];
    if (hit.kindWord === '' && hit.sourceText === '') continue;
    if (!focus.has(hit.victim)) continue;
    out.push(i);
  }
  out.sort((a, b) => (r.hits[a].t1Ds - r.hits[b].t1Ds) || (a - b));
  return out;
}

/** The row one hit belongs to: the kind, the source, its commander and its
 *  faction, which are the four cells the server wrote (R535). */
function rowKey(hit: ReplayHit): string {
  return [hit.kindWord, hit.sourceText, hit.sourceCommander,
    String(hit.sourceFaction)].join(SEP);
}

/**
 * The heads of the damage report, in the order a row writes its cells
 * (R535). The source is three columns since the owner's eye test: the hull
 * or the round, the commander it answers to, and the banner it sails
 * under. The stylesheet's track list is this list.
 */
export const REPORT_COLUMNS = [
  S.col_time, S.col_kind, S.col_source, S.col_commander, S.col_faction,
  S.col_hits, S.col_damage, S.col_shield, S.col_hull,
] as const;

/** Which cell of a row holds the faction, so the film tints that one. */
const FACTION_CELL = 4;

/** What joins the parts of a row key: a character no word of a report can
 *  hold, so two different sources can never make one key. */
const SEP = '\u0000';

/**
 * Folds one hit into a set of rows, in place, and answers the index of the
 * row it touched. A row is created by its first hit and its counters move
 * on every later one, which is exactly what the film does frame by frame
 * and what a seek does in one batch.
 */
function mergeHit(
  r: Replay, rows: ReportRow[], at: Map<string, number>, hit: ReplayHit,
): number {
  const key = rowKey(hit);
  const i = at.get(key);
  if (i === undefined) {
    at.set(key, rows.length);
    rows.push({
      key,
      unix: hit.unix,
      kind: hit.kindWord,
      source: hit.sourceText,
      commander: hit.sourceCommander,
      monster: r.hulls[hit.source]?.npc === NpcKind.NPC_KIND_MONSTER,
      faction: hit.sourceFaction,
      shots: Math.max(1, hit.shots),
      damageX10: hit.damageX10,
      shieldBeforeX10: hit.shieldBeforeX10,
      shieldAfterX10: hit.shieldAfterX10,
      hullBeforeX10: hit.hullBeforeX10,
      hullAfterX10: hit.hullAfterX10,
    });
    return rows.length - 1;
  }
  const row = rows[i];
  row.shots += Math.max(1, hit.shots);
  row.damageX10 += hit.damageX10;
  row.shieldAfterX10 = hit.shieldAfterX10;
  row.hullAfterX10 = hit.hullAfterX10;
  return i;
}

/**
 * The whole table at `tDs`: every hit on a focus hull that has landed,
 * folded into one row per source and kind, in first-seen order.
 */
export function aggregateRows(
  r: Replay, focus: ReadonlySet<number>, tDs: number,
): ReportRow[] {
  const rows: ReportRow[] = [];
  const at = new Map<string, number>();
  for (const i of reportOrder(r, focus)) {
    const hit = r.hits[i];
    if (hit.t1Ds > tDs) break;
    mergeHit(r, rows, at, hit);
  }
  return rows;
}

/** One fleet record, at rest. Every field the wire type states is set. */
function blankFleet(): FleetState {
  return {
    id: 0, faction: 0, xMpx: 0, yMpx: 0, cls: 0, hullPct: 100, shieldPct: 0,
    mission: 0, txMpx: 0, tyMpx: 0, own: false, captainId: 0, name: '', customName: false,
    slowed: false, design: 0, reloadSec: 0, rounds: [], activeRound: 0,
    torpedoRange: 0, hold: [], cargoCap: 0, captureLeftSec: 0,
    escorting: 0, focus: 0, weapType: 0, shieldType: 0, pxMpx: 0, pyMpx: 0,
    ownerPlayer: 0, ownerName: '', deep: false, readinessPct: 0, readinessFloorPct: 0,
    scanReadySec: 0, decoyReadySec: 0, sonarPx: 0, sonarShare: false, sonarShareLeftSec: 0,
    anchor: 0, anchorLeftSec: 0, beamLeftSec: 0,
    // The film draws no heal beam and no regen wave (R723): a replay's
    // records carry neither, so both marks stay at rest.
    tending: 0, aura: 0, npc: 0,
    // A record carries no design and no act of a monster: the monster
    // layer draws design 0 and reads the act from the motion (R2719).
    monsterDesign: 0, npcAct: 0, npcActLeftMs: 0,
  } as unknown as FleetState;
}

/** One round record, at rest. */
function blankTorpedo(): TorpedoState {
  return {
    id: 0, faction: 0, kind: 0, xMpx: 0, yMpx: 0, headingMrad: 0, runPct: 0,
  } as unknown as TorpedoState;
}

// ---------------------------------------------------------------------------
// The controller
// ---------------------------------------------------------------------------

/** What the film needs of the engine: the batches and the slot table. */
export interface FilmEngine extends BatchSource {
  layerNamed(name: string): Layer | undefined;
  setLayer(name: string, layer: Layer): void;
}

/** What the film needs of the camera: one call, and no reading. */
export interface FilmCamera {
  centerOn(x: number, y: number, zoom?: number): void;
}

/** The pan and the zoom the camera writes, which the film saves and puts
 *  back. It is the ENGINE's view: the camera keeps its own copy private. */
export interface FilmView {
  readonly x: number;
  readonly y: number;
  readonly scale: { readonly x: number };
}

/** The live layers the five slots hold, put back when the film closes. */
export interface FilmLiveLayers {
  fleets: Layer;
  torpedoes: Layer;
  fx: Layer;
  wake: Layer;
  monsters: Layer;
}

/** The film's own five layers, one per slot of `FILM_SLOTS`. */
interface FilmOwnLayers {
  fleets: FleetLayer;
  torpedoes: TorpedoLayer;
  fx: FxLayer;
  wake: WakeLayer;
  monsters: MonsterLayer;
}

export interface FilmDeps {
  engine: FilmEngine;
  camera: FilmCamera;
  /** The pan and the zoom, for the way back to the player's own view. */
  view: FilmView;
  /** The map's own four layers, for the way back. */
  live: FilmLiveLayers;
  /** `#film`, the film's glass inside `#ui`. */
  root: HTMLElement;
  /** `#ui`, which wears the `film` class while a film runs. */
  shell: HTMLElement;
  /** The drawing surface in CSS px, for the fit. */
  box: () => { w: number; h: number };
  /** The world's map scale (`Hello.tactical_scale`), injected: a replay
   *  second is a map second, so the pace is paid at this (R534). */
  mapScale?: () => number;
  /** The calm gate of the shield regen in map seconds
   *  (`Hello.regen_calm_map_sec`), for the opening hit of a fight (R1979). */
  regenCalmMapSec?: () => number;
  /** The round look the player picked. */
  torpedoStyle?: TorpedoStyle;
  /** The slow ring of a kind in px (`slowRingPx`, R2783), 0 for none. A
   *  page with no catalog leaves it out and draws no ring. */
  slowPx?: (kind: number) => number;
  /** The detonation look the player's World theme names (R2079), read when
   *  the film opens. A page that leaves it out draws the AUTO look. */
  detonation?: () => string;
  /** The splash and the damage of a round kind off the hello catalog, for
   *  the detonation look (R2079). A page with no catalog leaves it out, and
   *  the film reads the pack's rows. */
  torpedo?: (kind: number) => DetonationRound | undefined;
  /** Who is watching (`Hello.player_name`), for the damage figures: a
   *  replay names its commanders by NAME and carries no player id (R538). */
  playerName?: () => string;
  /** A dock-in still in flight has to be stopped before the chrome leaves. */
  abortDockIn?: () => void;
  /** The player pressed ✕ or the close key, after the film closed. A film
   *  of a replay link leaves for the chronicle book here (R1684). */
  onExit?: () => void;
  reduced?: () => boolean;
}

/** The `data-film` word of one control of the strip. */
type FilmAct = 'play' | 'restart' | 'stop' | 'report' | 'exit';

/** The control each of the film's three keys runs (R853). */
const FILM_KEY_ACTS: Readonly<Record<FilmKeyId, FilmAct>> = {
  close: 'exit', play: 'play', restart: 'restart',
};

/**
 * The film.
 *
 * `open` takes the chrome, the slots and the camera, in that order; `close`
 * gives all three back in the same order. Everything in between is one
 * `frame` call per engine frame, in place of the map's own.
 */
export class ReplayFilm {
  private readonly d: FilmDeps;
  private replay: Replay | null = null;
  private clock: FilmClock = { t: 0, playing: false, loops: 1, holdMs: 0 };
  private fit: Fit = { cx: 0, cy: 0, half: 1 };
  private sunk: ReadonlyMap<number, number> = new Map();
  /** When each hull broke off a window battle (R1451). */
  private withdrawn: ReadonlyMap<number, number> = new Map();
  private readonly rec = new FilmRecords();
  /** The time the last frame stood at, which is the low end of the window. */
  private lastT = 0;
  /** The next `applyState` replaces the whole picture. */
  private full = true;
  /** The world's map scale and the pace it buys, both read at open. */
  private scale = 1;
  private rate = PLAY_RATE_DS_PER_MS;

  // -- the damage figures (R538) --------------------------------------------
  /** The live figure layer, borrowed for the length of the film. */
  private figures: DamageLayer | null = null;
  /** The live gun-shot layer, borrowed the same way (R1007). */
  private shots: CombatLayer | null = null;
  /** The live work layer of the Sapper part, borrowed for the sea mine
   *  hits (R2657). */
  private work: SapperWorkLayer | null = null;
  /**
   * What the borrowed gun-shot layer asks about a hull, on the film's own
   * clock. Every answer comes out of the replay's own tracks, so a salvo
   * follows the REPLAY pose and not a live fleet of the same id.
   */
  private readonly shotFeeds: CombatFeeds = {
    at: (id) => this.anchorAt(id),
    // What the hull still holds on the film's own clock, so a shield mark
    // states the shield of the moment it plays (R1011).
    shieldOf: (id) => this.anchorAt(id)?.shield ?? -1,
    pose: (id) => {
      const p = this.anchorAt(id);
      if (!p) return null;
      const hull = this.hullOf.get(id);
      const q = this.poseBuf;
      q.x = p.x; q.y = p.y;
      q.head = hull ? hull.head : 0;
      q.faction = hull ? hull.faction : 0;
      q.cls = hull ? hull.cls : 0;
      return q;
    },
    // A replay names the shooter's class on the record, so the layer never has
    // to read a hull off the water to find the family (R1009).
    near: () => null,
    ownerOf: () => 0,
    // A replay second is a map second, so the calm gate takes the film's own
    // scale (R1979). A host that states no gate makes every hit open.
    fightGapSec: () => (this.d.regenCalmMapSec?.() ?? 0) / this.scale,
  };
  private readonly poseBuf = { x: 0, y: 0, head: 0, faction: 0, cls: 0 };
  /** The hull record of every track, by its sim fleet id. Built at open. */
  private readonly hullOf = new Map<number, { faction: number; cls: number; head: number }>();
  /** Who is watching, read at open: the film classifies by name. */
  private myName = '';
  /** The track of every hull, by its sim fleet id. It is built at open, so
   *  the anchor the layer calls costs one map read and no allocation. */
  private readonly trackOf = new Map<number, ReplayTrack>();
  /** What the borrowed layer asks for: where the struck hull is NOW, on
   *  the film's own clock. */
  private readonly anchorAt = (id: number): Pose | null => {
    const tr = this.trackOf.get(id);
    return tr ? poseAt(tr, this.clock.t) : null;
  };

  // -- the camera -----------------------------------------------------------
  /** The window the film opened on: where the camera goes back to when the
   *  fight fits again. */
  private home: FilmCam = { cx: 0, cy: 0, zoom: 1 };
  /** Where the camera stands now. The film writes the camera and never
   *  reads it, so this is its own copy of what it wrote. */
  private cam: FilmCam = { cx: 0, cy: 0, zoom: 1 };
  /** The glass the fight may use, in CSS px from the centre. Measured on
   *  open, on a resize and when the report is shown or hidden — never per
   *  frame, because a rect is a layout read (§10.3). */
  private safeRect: Bounds = { x0: -1, y0: -1, x1: 1, y1: 1 };
  /** How long the fight has fitted at `home` without a break, in ms. The
   *  camera goes back only when it passes the dwell (R541). */
  private homeHeld = 0;
  /** The canvas box the last fit was made for, so a resize can tell a real
   *  reshape from a telephone's own chrome (R580). */
  private lastFitBox: FilmBox | null = null;
  private resizeHandler: (() => void) | null = null;
  /** The watch on the report's own box (R581). */
  private reportWatch: ResizeObserver | null = null;

  /** The film's own layers, built on the first open and kept (see `open`). */
  private own: FilmOwnLayers | null = null;

  // -- what open() saved ----------------------------------------------------
  private savedVisible = new Map<string, boolean>();
  private savedCam: { x: number; y: number; zoom: number } | null = null;

  // -- the chrome -----------------------------------------------------------
  private readonly titleEl = document.createElement('span');
  /** The countdown of the loop's hold, beside the tag (R535). */
  private readonly holdEl = document.createElement('span');
  private readonly clockEl = document.createElement('span');
  private readonly loopEl = document.createElement('span');
  private readonly playBtn = document.createElement('button');
  private readonly scrubber = document.createElement('input');
  /** The damage report beside the film, and the rows it appends into. */
  private readonly reportEl = document.createElement('div');
  private readonly reportHead = document.createElement('div');
  private readonly reportRows = document.createElement('div');
  /** The damage records the report accounts for, earliest first. */
  private order: number[] = [];
  /** How many of them the table has already folded in. */
  private rowsShown = 0;
  /** The hulls the reference put in focus: the table counts these only. */
  private focus: ReadonlySet<number> = new Set();
  /** The table itself, and the row element and cells of every row. */
  private rows: ReportRow[] = [];
  private rowAt = new Map<string, number>();
  private rowEls: HTMLElement[] = [];
  /** What the readout last said, so a frame writes no DOM it need not. */
  private lastClockText = '';
  private lastLoopText = '';
  private lastHoldText = '';
  private lastScrub = -1;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;
  /** The top letterbox bar, kept so the safe rect can measure its height
   *  rather than mirror the stylesheet's `film_bar_vh`. */
  private topBar: HTMLElement | null = null;

  constructor(deps: FilmDeps) {
    this.d = deps;
    this.build();
  }

  isOpen(): boolean { return this.replay !== null; }

  // -- the glass ------------------------------------------------------------

  /** What each control of the strip does. The keys run the same entries. */
  private readonly acts: Record<FilmAct, () => void> = {
    play: () => this.setPlaying(!this.clock.playing),
    restart: () => { this.seek(0); this.setPlaying(true); },
    stop: () => { this.seek(0); this.setPlaying(false); },
    report: () => {
      this.d.root.classList.toggle('noreport');
      // The report is part of the glass the fight may use, so hiding it
      // hands the film the room it took.
      this.measure();
    },
    exit: () => {
      this.close();
      this.d.onExit?.();
    },
  };

  /**
   * The film's chrome, built ONE time. A frame writes text into three
   * nodes and never markup, so no frame of a film touches the layout
   * (§10, no per-frame DOM churn).
   *
   * Every button acts on `pointerdown`: a control destroyed between the
   * press and the release swallows the click (client/CLAUDE.md).
   */
  private build(): void {
    const root = this.d.root;
    const el = (tag: string, cls: string): HTMLElement => {
      const e = document.createElement(tag);
      e.className = cls;
      return e;
    };
    const bar = (cls: string) => {
      const b = el('div', `fbar ${cls}`);
      b.append(el('i', 'holes'));
      return b;
    };
    const grain = el('div', 'grain');
    const scan = el('div', 'scan');
    const top = bar('top');
    this.topBar = top;
    const tag = el('div', 'tag');
    const dot = el('i', 'dot');
    const word = el('span', 'word');
    word.textContent = S.film_tag;
    this.titleEl.className = 'ftitle';
    // The hold's countdown (R535). It stands at the head of the tag, where
    // a reader is already looking, and it is empty while the film runs.
    this.holdEl.className = 'fhold';
    tag.append(dot, word, this.holdEl, this.titleEl);
    const meta = el('div', 'meta');
    this.clockEl.className = 'fclock';
    this.loopEl.className = 'floop';
    meta.append(this.clockEl, this.loopEl);
    top.append(tag, meta);

    const bot = bar('bot');
    const ctl = el('div', 'ctl');
    // Every glyph of the strip asks for its text form (R579), so the
    // rewind and the play triangle cannot draw as emoji beside the rest.
    const button = (act: FilmAct, glyph: string, title: string, label = '') => {
      const b = document.createElement('button');
      b.dataset.film = act;
      b.textContent = label === '' ? textGlyph(glyph) : `${textGlyph(glyph)} ${label}`;
      b.title = title;
      return b;
    };
    this.playBtn.dataset.film = 'play';
    this.playBtn.textContent = textGlyph('❚❚');
    this.playBtn.title = S.pause_title;
    this.scrubber.type = 'range';
    this.scrubber.className = 'scrub';
    this.scrubber.min = '0';
    this.scrubber.step = '1';
    this.scrubber.title = S.scrub_title;
    const reportBtn = button('report', '▤', S.report_toggle_title, S.report_label);
    reportBtn.classList.add('wide');
    ctl.append(
      button('restart', '⏮', S.restart_title),
      this.playBtn,
      button('stop', '■', S.stop_title),
      this.scrubber,
      reportBtn,
      button('exit', '✕', S.exit_title),
    );
    bot.append(ctl);

    // The damage report (R527, R531, R534). Text is DOM and never canvas
    // (§1). A row is appended by its first damage and its cells are
    // written as more lands, so a frame of a film costs one append or one
    // set of writes, and never a rebuilt panel.
    this.reportEl.className = 'report';
    this.reportHead.className = 'rhead';
    this.reportHead.textContent = S.report_title;
    const cols = el('div', 'rcols');
    for (const word of REPORT_COLUMNS) {
      const cell = document.createElement('span');
      cell.textContent = word;
      cols.append(cell);
    }
    this.reportRows.className = 'rrows';
    this.reportEl.append(this.reportHead, cols, this.reportRows);

    root.append(grain, scan, this.reportEl, top, bot);

    root.addEventListener('pointerdown', (e) => {
      const btn = (e.target as HTMLElement | null)?.closest('button');
      const act = btn instanceof HTMLElement ? btn.dataset.film : undefined;
      if (!act) return;
      e.preventDefault();
      this.acts[act as FilmAct]?.();
    });
    this.scrubber.addEventListener('input', () => {
      this.seek(Number(this.scrubber.value));
      this.setPlaying(false);
    });
  }

  // -- open and close -------------------------------------------------------

  /**
   * Opens a film: the chrome, the slots, the camera, the clock.
   *
   * The layers are built on the first open and kept for the session. Each
   * one owns instance batches of the engine, and a set of them per film
   * would leak a set of buffers per film.
   */
  open(r: Replay): void {
    if (this.isOpen()) this.close();
    const d = this.d;
    const engine = d.engine;
    this.replay = r;
    this.fit = fitOf(r);
    this.sunk = sinkTimes(r);
    this.withdrawn = withdrawTimes(r);
    this.clock = { t: 0, playing: true, loops: 1, holdMs: 0 };
    this.lastT = 0;
    this.full = true;
    // A replay second is a MAP second, so the film is paid at the world's
    // own map scale as well as at the rate the player asked for (R534).
    this.scale = Math.max(1e-6, d.mapScale?.() ?? 1);
    this.rate = playRateDsPerMs(this.scale);

    const own = this.own ?? (this.own = this.buildLayers(engine));
    // The detonation look of the player's theme, and the splash of each
    // round off the catalog (R2079), as the live map draws them.
    own.fx.setDetonationLook(d.detonation?.() ?? DETONATION_AUTO);
    own.fx.setRounds(d.torpedo ?? packRound);

    // 1. the chrome. A dock-in in flight would fight the travel.
    d.abortDockIn?.();
    d.root.classList.toggle('still', (d.reduced ?? reducedMotion)());
    d.root.classList.add('on');
    d.shell.classList.add('film');

    // 2. the slots: the four the film borrows, then the ones it hides. The
    // visibility is saved by SLOT and not by layer, so a hello that
    // replaced a layer under us still gets its flag back.
    for (const slot of FILM_SLOTS) {
      own[slot].setVisible(true);
      engine.setLayer(slot, own[slot]);
    }
    this.savedVisible.clear();
    for (const slot of FILM_HIDDEN_SLOTS) {
      const l = engine.layerNamed(slot);
      if (!l) continue;
      this.savedVisible.set(slot, l.visible);
      l.setVisible(false);
    }
    // The damage figures (R538). The live ones belong to the time the film
    // left, so they go; then the film borrows the layer, and every figure
    // of the fight it plays follows the REPLAY pose of the hull it struck.
    const liveDamage = engine.layerNamed('damage');
    this.figures = liveDamage instanceof DamageLayer ? liveDamage : null;
    // The work layer of the Sapper part (R2657): the film plays its sea
    // mine hits there, and the layer drops the looks of today.
    const liveWork = engine.layerNamed('sapperwork');
    this.work = liveWork instanceof SapperWorkLayer ? liveWork : null;
    this.work?.setFilming(true);
    this.trackOf.clear();
    this.hullOf.clear();
    for (const tr of r.tracks) {
      const hull = r.hulls[tr.hull];
      if (!hull) continue;
      this.trackOf.set(hull.id, tr);
      // The heading is not on a track, so a sinking hull takes the bearing of
      // its own last leg (R1337). The layer reads it off this record.
      this.hullOf.set(hull.id, { faction: hull.faction, cls: hull.cls, head: lastLegHeading(tr) });
    }
    this.myName = d.playerName?.() ?? '';
    this.figures?.clear();
    this.figures?.setAnchorSource(this.anchorAt);
    // A figure on film is larger than on the map, because the film zooms
    // in on the hull it stands beside (R552).
    this.figures?.setScale(REPLAY_FILM_FIGURE_SCALE);
    // The gun shots and the hits (R1007), borrowed the same way. The film's own
    // hold keeps a mark from covering the hull it struck at the film's zoom.
    const liveCombat = engine.layerNamed('combat');
    this.shots = liveCombat instanceof CombatLayer ? liveCombat : null;
    if (this.shots) {
      this.shots.holdZoom = REPLAY_FILM_FX_HOLD_ZOOM;
      this.shots.setFeeds(this.shotFeeds);
    }

    // 3. the camera. The film's window, and the way back to the player's.
    const view = d.view;
    const z = view.scale.x || 1;
    this.savedCam = {
      x: (window.innerWidth / 2 - view.x) / z,
      y: (window.innerHeight / 2 - view.y) / z,
      zoom: z,
    };
    this.fitCamera();

    // 4. the readout, and the keys that drive it.
    this.titleEl.textContent = r.title;
    this.reportHead.textContent = r.title || S.report_title;
    this.focus = focusHulls(r);
    this.order = reportOrder(r, this.focus);
    this.clearRows();
    // A record the log timed at zero is already news when the film opens.
    this.growRows(0, false);
    this.scrubber.max = String(Math.max(1, r.durationDs));
    this.lastClockText = this.lastLoopText = '';
    this.lastHoldText = this.holdEl.textContent = '';
    this.lastScrub = -1;
    this.setPlaying(true);
    this.installKeys();
    this.installResize();
    this.installReportWatch();
    this.render();
  }

  /** The film's five layers, in the order of `FILM_SLOTS`. */
  private buildLayers(engine: FilmEngine): FilmOwnLayers {
    const fleets = new FleetLayer(engine);
    // Nobody is flying in a film, so every hull carries its pair of bars.
    fleets.showAllBars = true;
    const torpedoes = new TorpedoLayer(engine, this.d.torpedoStyle);
    const fx = new FxLayer(engine);
    // A film zooms in on a fight, and a blast that grew with the map
    // covered the hull it struck: past the hold the blasts keep their
    // screen size (R552).
    fx.holdZoom = REPLAY_FILM_FX_HOLD_ZOOM;
    const wake = new WakeLayer(engine);
    wake.allFactions = true;
    // The monster bodies (R2719), at the place and the bow of the film's
    // own fleet layer, which takes the mark of each body away. A world
    // without `pirates` records no monster, so the gate stays open.
    const monsters = new MonsterLayer(engine, {
      pose: (id, out) => fleets.signaturePose(id, out),
      foe: (x, y, r) => fleets.nearestFoe(x, y, 0, r),
      foes: (x, y, r, out) => fleets.foesWithin(x, y, 0, r, out),
      mapSecPerWallSec: () => this.scale,
    });
    monsters.setGate(true);
    fleets.setHullSuppressed(monsters.drawn);
    return { fleets, torpedoes, fx, wake, monsters };
  }

  /**
   * Puts the film's window on the canvas as it stands, and remembers it as
   * home.
   *
   * `snap` moves the camera there AT ONCE and puts the dwell back to
   * nought. An open snaps, and so does a resize that really changed the
   * shape of the glass (R534): that film must not wait for the follow to
   * ease the fight back into view.
   *
   * A re-fit that did NOT change the shape does not snap (R580, R581). A
   * telephone fires `resize` when the browser chrome collapses or expands
   * and when the application comes back from the background, and the
   * report's own height changes every time a row lands. The fight has not
   * moved in any of those, so the film re-measures the glass, takes the new
   * home, and lets the follow ease the camera into it (R541).
   */
  private fitCamera(snap = true): void {
    const box = this.d.box();
    this.lastFitBox = { w: box.w, h: box.h };
    this.home = {
      cx: this.fit.cx, cy: this.fit.cy, zoom: filmZoom(this.fit, box.w, box.h),
    };
    this.measure();
    if (!snap) return;
    this.cam = { ...this.home };
    // The camera IS home again, so the dwell starts over (R541).
    this.homeHeld = 0;
    this.d.camera.centerOn(this.cam.cx, this.cam.cy, this.cam.zoom);
  }

  /** Re-fits after an event that may or may not have reshaped the glass
   *  (R580): it snaps only when the canvas box really changed. */
  private refit(): void {
    if (!this.isOpen()) return;
    this.fitCamera(boxReshaped(this.lastFitBox, this.d.box()));
  }

  /**
   * Measures the glass the fight may use: the viewport, less the two
   * letterbox bars, less the damage report where it stands. The answer is
   * in CSS px FROM THE CENTRE, because the camera centres what it is
   * given.
   *
   * It is a layout read, so it runs on an open, on a resize, on the report
   * toggle and when the report's own box changes — never inside a frame
   * (§10.3). `safeGlass` states the rule and the test reads it.
   */
  private measure(): void {
    const w = window.innerWidth || 1;
    const h = window.innerHeight || 1;
    // The bar and the report are chrome under the UI scale, and the glass
    // is the canvas, so both are read in viewport px (R1556).
    const bar = this.topBar ? viewportRect(this.topBar).height : 0;
    let report: Bounds | null = null;
    if (!this.d.root.classList.contains('noreport')) {
      const r = viewportRect(this.reportEl);
      if (r.width > 0 && r.height > 0) {
        report = { x0: r.left, y0: r.top, x1: r.right, y1: r.bottom };
      }
    }
    const g = safeGlass(w, h, bar, report);
    this.safeRect = {
      x0: g.x0 - w / 2, y0: g.y0 - h / 2, x1: g.x1 - w / 2, y1: g.y1 - h / 2,
    };
  }

  /** A resize re-fits the window, and re-applies the camera when the glass
   *  really changed shape (R580). */
  private installResize(): void {
    if (this.resizeHandler) return;
    const h = () => this.refit();
    this.resizeHandler = h;
    window.addEventListener('resize', h);
  }

  private removeResize(): void {
    if (!this.resizeHandler) return;
    window.removeEventListener('resize', this.resizeHandler);
    this.resizeHandler = null;
  }

  /**
   * Watches the damage report's own box (R581).
   *
   * The report grows as rows land, and on a telephone it stands at the foot
   * and takes the glass off the fight. `measure()` is a layout read, so a
   * frame must never make one; a `ResizeObserver` is event-driven and
   * reports the box the browser already computed, which §10.3 allows. The
   * re-fit takes the same rule as a resize, so the fight EASES into the new
   * centre and never jumps.
   */
  private installReportWatch(): void {
    if (this.reportWatch || typeof ResizeObserver === 'undefined') return;
    const o = new ResizeObserver(() => this.refit());
    o.observe(this.reportEl);
    this.reportWatch = o;
  }

  private removeReportWatch(): void {
    if (!this.reportWatch) return;
    this.reportWatch.disconnect();
    this.reportWatch = null;
  }

  /** Gives the chrome, the slots and the camera back, in the same order. */
  close(): void {
    if (!this.isOpen()) return;
    const { engine } = this.d;
    this.replay = null;
    this.clock = { t: 0, playing: false, loops: 1, holdMs: 0 };

    this.d.root.classList.remove('on', 'still');
    this.d.shell.classList.remove('film');
    this.clearRows();
    this.order = [];

    for (const slot of FILM_SLOTS) {
      const live = this.d.live[slot];
      live.setVisible(true);
      engine.setLayer(slot, live);
    }
    for (const [slot, was] of this.savedVisible) {
      engine.layerNamed(slot)?.setVisible(was);
    }
    this.savedVisible.clear();
    // The rounds and the effects go now, because both age on wall time and
    // would burn on over the live map. The FLEET layer keeps its records:
    // the next film opens with a full frame and a snap, which fades every
    // hull of the last one out on the first frame. The figures go the same
    // way, and the layer goes back to the map: the live feed owns it again
    // from here (R538).
    this.clearTransient();
    this.figures?.setScale(1);
    this.figures?.setAnchorSource(null);
    this.figures = null;
    // The work layer goes back to the live frames (R2657).
    this.work?.setFilming(false);
    this.work = null;
    // The gun-shot layer goes back to the map, and its hold with it (R1007).
    if (this.shots) {
      this.shots.setFeeds(null);
      this.shots.holdZoom = 0;
      this.shots = null;
    }
    this.trackOf.clear();
    this.hullOf.clear();

    if (this.savedCam) {
      this.d.camera.centerOn(this.savedCam.x, this.savedCam.y, this.savedCam.zoom);
      this.savedCam = null;
    }
    this.removeKeys();
    this.removeResize();
    this.removeReportWatch();
    this.lastFitBox = null;
  }

  // -- the transport --------------------------------------------------------

  private setPlaying(on: boolean): void {
    this.clock = { ...this.clock, playing: on, holdMs: on ? this.clock.holdMs : 0 };
    // The pause bars and the play triangle both take the text form (R579).
    this.playBtn.textContent = textGlyph(on ? '❚❚' : '▶');
    this.playBtn.title = on ? S.pause_title : S.play_title;
  }

  /**
   * Goes to a time. The rounds and the effects belong to the time we left,
   * so both go; the hulls are put where the new time says they are, with no
   * ease across the gap.
   */
  private seek(tDs: number): void {
    const end = this.replay?.durationDs ?? 0;
    this.clock = { ...this.clock, t: Math.max(0, Math.min(end, tDs)), holdMs: 0 };
    // The window that fires the effects is half open, so the low end sits
    // one hair below the new time: a launch recorded AT the seek time still
    // fires, and nothing before it does.
    this.lastT = this.clock.t - SEEK_EPS_DS;
    this.clearTransient();
    this.full = true;
    // The report goes back with the film: the table is folded again from
    // every hit before the new time, in ONE batch, so a scrub costs one
    // pass and not one per frame of the time it crossed.
    this.clearRows();
    this.growRows(this.clock.t, false);
  }

  /** Takes the rounds, the effects, the monsters and the damage figures
   *  off the film: all of them belong to the time the film leaves. */
  private clearTransient(): void {
    this.own?.torpedoes.clear();
    this.own?.fx.clear();
    this.own?.monsters.clear();
    this.figures?.clear();
  }

  // -- the frame ------------------------------------------------------------

  /**
   * One engine frame of a film, in place of `MapScene.frame`.
   *
   * `main.ts` calls this instead of the map's own feed, because that feed
   * rewrites the visibility of the fleets, the rounds, the wake, the sonar
   * and the fluff on every frame and would take the film's slots back.
   */
  frame(f: FrameInfo): void {
    const r = this.replay;
    const own = this.own;
    if (!r || !own) return;
    // `f.dtMs` is one BOUNDED step: `frame.ts` clamps it, and the engine
    // stops on `visibilitychange` and takes its clock again on the way back
    // (R580). So a telephone that leaves the application for a minute comes
    // back at the frame it left, and the film cannot run its whole length
    // and loop while nobody watched.
    const step = stepClock(this.clock, f.dtMs, r.durationDs, REPLAY_LOOP_HOLD_MS, this.rate);
    this.clock = step.clock;
    if (step.seeked) this.seek(0);
    const t = this.clock.t;

    fleetRecordsAt(r, t, this.sunk, this.rec, this.withdrawn);
    own.fleets.applyState(this.rec.fleets, this.rec.removed, this.full);
    own.monsters.applyState(this.rec.fleets, this.rec.removed, this.full);
    if (this.full) own.fleets.snap();
    this.full = false;

    torpedoRecordsAt(r, t, this.rec);
    own.torpedoes.apply(this.rec.torpedoes);

    // The window is the latch: a seek moves both ends together and fires
    // nothing, which is why a restart never replays the last frame's boom.
    effectsBetween(r, this.lastT, t, this.sunk, this.rec);
    damageBetween(r, this.lastT, t, this.myName, this.rec);
    this.fire(own);
    this.lastT = t;

    // Every hull of a film may leave a scar (R531): the flag is on the
    // layer, and the faction handed over here is nobody's.
    own.wake.feed(own.fleets.info(), 0);

    this.follow(f.dtMs);

    // A row lands as its source first lands a blow, and its counters move
    // as that source lands more (R534).
    this.growRows(t, true);
    this.render();
  }

  /**
   * The follow camera (R534): every hull and every round stays on the
   * glass.
   *
   * The bounds are what this frame just handed the layers, so the camera
   * follows what is drawn and nothing else. The move is a fraction of the
   * gap, so the camera reads as an operator and never as a cut.
   *
   * The dwell is the second half of the rule (R541): the camera goes back
   * to the opening window only after the fight has fitted there for
   * `camera_home_dwell_ms`, and one round in the water puts the clock
   * back. Between the two the camera holds where it stands.
   */
  private follow(dtMs: number): void {
    const bounds = recordBounds(this.rec);
    if (!bounds) return;
    this.homeHeld = homeDwell(this.homeHeld, fitsAt(bounds, this.safeRect, this.home), dtMs);
    const want = frameTarget(
      bounds, this.safeRect, this.cam, this.home, homeDue(this.homeHeld),
    );
    const k = easeFraction(dtMs);
    if (k <= 0) return;
    const cam = this.cam;
    const cx = cam.cx + (want.cx - cam.cx) * k;
    const cy = cam.cy + (want.cy - cam.cy) * k;
    const zoom = cam.zoom + (want.zoom - cam.zoom) * k;
    // An ease closes a gap and never reaches it, so a settled camera would
    // write a hair of movement for ever. Below what an eye can see, the
    // film stops writing: the camera's own `onChange` reports a viewport
    // (§10.3), and a film must not keep that timer alive for nothing.
    if (Math.abs(cx - cam.cx) < CAM_STILL_PX
      && Math.abs(cy - cam.cy) < CAM_STILL_PX
      && Math.abs(zoom - cam.zoom) < CAM_STILL_ZOOM) return;
    // The copy is the film's own (`fitCamera` copies home into it), so a
    // frame writes it in place and allocates nothing.
    cam.cx = cx; cam.cy = cy; cam.zoom = zoom;
    this.d.camera.centerOn(cx, cy, zoom);
  }

  /** Hands this frame's effects to the layer, in the order they were read. */
  private fire(own: FilmOwnLayers): void {
    const { fx } = own;
    const shots = this.shots;
    const gun = shots !== null && shots.enabled;
    for (const e of this.rec.effects) {
      switch (e.kind) {
        case 'launch': fx.launch(e.x, e.y, e.faction); break;
        case 'detonation': {
          // The detonation look, out to the splash of the round's kind
          // (R2079): a `ReplayHit` names the kind of its round.
          // A record with no launch point (0, 0) takes no course (R2842).
          const sourced = e.sx !== 0 || e.sy !== 0;
          fx.detonate(e.x, e.y, e.faction, e.torpedo, sourced ? e.sx : e.x, sourced ? e.sy : e.y);
          // A Torpor slows what is near it (R2783): a ring out to the
          // catalog's splash, where the page wires the catalog.
          const slow = this.d.slowPx?.(e.torpedo) ?? 0;
          if (slow > 0) fx.slowRing(e.x, e.y, e.faction, slow);
          break;
        }
        case 'ring': fx.ring(e.x, e.y, e.faction); break;
        // A gun pulse and a sinking are the gun-shot layer's (R1007). With
        // `#shots=0` the film draws the ring and the boom it drew before.
        case 'hit':
          // A monster's hit plays the strike of its body (R2719).
          if (monsterKindOf(e.cls) >= 0) own.monsters.struck(e.sx, e.sy, e.x, e.y);
          if (gun) this.fireShots(shots!, e, 'hit');
          else fx.hit(e.x, e.y, e.faction);
          break;
        // A mark has no shooter, so it never reaches the gun-shot layer.
        case 'mark': fx.hit(e.x, e.y, e.faction); break;
        // A sea mine went off on the struck hull (R2657).
        case 'seamine': this.work?.burst(e.x, e.y, e.seaMine, e.faction, [e.x, e.y]); break;
        case 'explosion':
          if (gun) this.fireShots(shots!, e, 'sink');
          else fx.explosion(e.x, e.y, e.faction);
          break;
      }
    }
    // The figures of this window. A figure ages on WALL time, because it is
    // a screen thing: a film that runs fast does not stretch the read (R538).
    const figures = this.figures;
    if (figures) for (const h of this.rec.damage) figures.show(h);
  }

  /** One film record handed to the borrowed gun-shot layer (R1007). */
  private fireShots(shots: CombatLayer, e: FilmEffect, kind: 'hit' | 'sink'): void {
    const ev = this.shotEvent;
    ev.kind = kind;
    ev.victim = e.victim;
    ev.x = e.x; ev.y = e.y;
    ev.sourceX = e.sx; ev.sourceY = e.sy;
    ev.faction = e.faction;
    ev.victimFaction = e.victimFaction;
    ev.actor = 0;
    ev.shieldPct = e.shieldPct;
    ev.shooterCls = e.cls;
    shots.fire(ev);
  }

  /** The one reused event the film hands the layer (R131). */
  private readonly shotEvent: CombatEvent = {
    kind: 'hit', victim: 0, x: 0, y: 0, sourceX: 0, sourceY: 0,
    faction: 0, victimFaction: 0, actor: 0, shieldPct: -1, shooterCls: 0,
  };

  /** Takes every row off the glass. A seek and an open both do it. */
  private clearRows(): void {
    this.rowsShown = 0;
    this.rows = [];
    this.rowAt = new Map();
    this.rowEls = [];
    this.reportRows.replaceChildren();
  }

  /**
   * Folds every damage that has landed by `tDs` into the table.
   *
   * A hit either opens a row or moves the counters of the row its source
   * already holds, so the table is an account and never a log (R534). The
   * cells of a touched row are written and nothing else is: no frame of a
   * film rebuilds the panel.
   *
   * `lit` marks a row that MOVED while the film runs, so the eye catches
   * what just happened; a batch rebuilt after a seek lights nothing,
   * because every one of those rows is old news. The panel then scrolls to
   * the foot, which is the one layout read a landing row costs.
   */
  private growRows(tDs: number, lit: boolean): void {
    const r = this.replay;
    if (!r) return;
    let touched = 0;
    while (this.rowsShown < this.order.length
      && r.hits[this.order[this.rowsShown]].t1Ds <= tDs) {
      const hit = r.hits[this.order[this.rowsShown]];
      const i = mergeHit(r, this.rows, this.rowAt, hit);
      let el = this.rowEls[i];
      const fresh = !el;
      if (!el) {
        el = document.createElement('div');
        el.className = 'rrow';
        for (let c = 0; c < REPORT_COLUMNS.length; c++) {
          el.append(document.createElement('span'));
        }
        // The faction cell wears its own banner (R535). A row's faction is
        // part of its key, so the ink is written once, when the row lands.
        const cell = el.children[FACTION_CELL] as HTMLElement;
        const faction = this.rows[i].faction;
        if (faction > 0) cell.style.color = hex(factionColor(faction));
        this.rowEls[i] = el;
        this.reportRows.append(el);
      }
      this.writeRow(el, this.rows[i], lit, fresh);
      this.rowsShown++;
      touched++;
    }
    if (touched > 0) this.reportRows.scrollTop = this.reportRows.scrollHeight;
  }

  /**
   * Writes one row's nine cells.
   *
   * A row that lands lights whole; a row that MOVED lights the cells that
   * moved, so the eye reads what changed and not the whole line again.
   *
   * The highlight is restarted by taking every class off, reading the
   * layout once and putting the class back, because a row that moves twice
   * must blink twice. It costs one read per damage that lands, which is
   * rare — never one per frame.
   */
  private writeRow(
    el: HTMLElement, row: ReportRow, lit: boolean, fresh: boolean,
  ): void {
    const cells = el.children;
    const text = [
      utcClock(row.unix),
      // The kind opens a column, so it opens with a capital (R535).
      capitalise(row.kind),
      row.source,
      commanderCell(row),
      factionCell(row.faction),
      String(row.shots),
      points(row.damageX10),
      S.span({ from: points(row.shieldBeforeX10), to: points(row.shieldAfterX10) }),
      S.span({ from: points(row.hullBeforeX10), to: points(row.hullAfterX10) }),
    ];
    const moved: Element[] = [];
    for (let i = 0; i < text.length; i++) {
      const cell = cells[i];
      if (!cell || cell.textContent === text[i]) continue;
      cell.textContent = text[i];
      moved.push(cell);
    }
    el.classList.remove('lit');
    for (const cell of cells) cell.classList.remove('lit');
    if (!lit) return;
    void el.offsetWidth;
    if (fresh) el.classList.add('lit');
    else for (const cell of moved) cell.classList.add('lit');
  }

  /** The readout. Each node is written only when its text changed. */
  private render(): void {
    const r = this.replay;
    if (!r) return;
    // The timecode is WALL-equivalent seconds: a replay decisecond is a
    // map decisecond, and the map ran at the world's own scale (R534).
    const text = S.clock({
      at: Math.floor(this.clock.t / 10 / this.scale),
      total: Math.floor(r.durationDs / 10 / this.scale),
      rate: REPLAY_PLAY_RATE_X,
    });
    if (text !== this.lastClockText) {
      this.lastClockText = text;
      this.clockEl.textContent = text;
    }
    const loop = S.loop_label({ n: this.clock.loops });
    if (loop !== this.lastLoopText) {
      this.lastLoopText = loop;
      this.loopEl.textContent = loop;
    }
    // The hold's countdown (R535). It is whole seconds, so a five-second
    // hold costs five DOM writes and not one for every frame of it.
    const hold = holdWord(this.clock.holdMs);
    if (hold !== this.lastHoldText) {
      this.lastHoldText = hold;
      this.holdEl.textContent = hold;
    }
    const at = Math.round(this.clock.t);
    if (at !== this.lastScrub) {
      this.lastScrub = at;
      this.scrubber.value = String(at);
    }
  }

  // -- the keys -------------------------------------------------------------

  /**
   * The film's own three keys, on the capture phase, so they beat the map's
   * handler while a film is up. `main.ts` suspends that handler as well —
   * this is the half that ACTS, and that is the half that stands down.
   */
  private installKeys(): void {
    if (this.keyHandler) return;
    const h = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      // The film's three bindings and nothing else (R853): a chord none of
      // them claims matches nothing and stays the browser's.
      const id = matchKey(e, KEYS.film);
      if (id === null) return;
      e.preventDefault();
      this.acts[FILM_KEY_ACTS[id]]();
      e.stopPropagation();
    };
    this.keyHandler = h;
    window.addEventListener('keydown', h, true);
  }

  private removeKeys(): void {
    if (!this.keyHandler) return;
    window.removeEventListener('keydown', this.keyHandler, true);
    this.keyHandler = null;
  }
}
